import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';
import '../students/StudentAttendancePage.css';

const AdminStudentAttendance = () => {
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [attendancePercentage, setAttendancePercentage] = useState('N/A');
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { id: studentId } = useParams();

  // Get student name from location state
  const studentName = location.state?.studentName || 'Student';

  // Fetch attendance data using admin service
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!studentId) {
        setError('No student ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Admin fetching attendance for student:', studentId);
        
        // Get attendance data from admin service
        const attendanceRecords = await adminService.getStudentAttendance(studentId);
        console.log('Admin attendance data received:', attendanceRecords);

        if (Array.isArray(attendanceRecords)) {
          // Process and format the attendance data
          const formattedAttendance = attendanceRecords.map(record => {
            // Format date
            const date = new Date(record.date);
            const formattedDate = date.toLocaleDateString('en-US', { 
              weekday: 'long',
              year: 'numeric', 
              month: 'long', 
              day: 'numeric'
            });
              
            // Determine colors based on status
            let statusColor, backgroundColor, statusText;
              
            switch(record.status) {
              case 'present':
                statusColor = '#2e7d32';
                backgroundColor = '#e8f5e9';
                statusText = 'Present';
                break;
              case 'absent':
                statusColor = '#d32f2f';
                backgroundColor = '#ffebee';
                statusText = 'Absent';
                break;
              case 'late':
                statusColor = '#ff8f00';
                backgroundColor = '#fff8e1';
                statusText = 'Late';
                break;
              default:
                statusColor = '#616161';
                backgroundColor = '#f5f5f5';
                statusText = 'Unknown';
            }
              
            return {
              id: record._id,
              date: formattedDate,
              status: statusText,
              statusColor,
              backgroundColor,
              className: record.halaqaName || 'Unknown Class', // Now populated by backend
              isPast: isPast(record.date)
            };
          });
            
          setAttendance(formattedAttendance);
          
          // Calculate attendance percentage (counting both 'Present' and 'Late' as attendance)
          const presentCount = formattedAttendance.filter(a => 
            a.status === 'Present' || a.status === 'Late'
          ).length;
          const totalCount = formattedAttendance.length;
          const attendanceRate = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;
          setAttendancePercentage(attendanceRate);
        } else {
          setAttendance([]);
          setAttendancePercentage(0);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching admin attendance data:', err);
        setError('Failed to load attendance data');
        setAttendance([]);
        setAttendancePercentage(0);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [studentId]);

  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(date) < today;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading attendance history...</p>
      </div>
    );
  }

  // Calculate summary stats for admin view
  const presentCount = attendance.filter(a => a.status === 'Present').length;
  const absentCount = attendance.filter(a => a.status === 'Absent').length;
  const lateCount = attendance.filter(a => a.status === 'Late').length;

  return (
    <div className="student-attendance-page">
      <div className="page-header">
        <h1>{`${studentName}'s Attendance History (Admin View)`}</h1>
        <div style={{ 
          fontSize: '14px', 
          color: '#666', 
          marginTop: '4px',
          padding: '4px 8px',
          backgroundColor: '#fff3cd',
          borderRadius: '4px',
          border: '1px solid #ffeaa7',
          display: 'inline-block'
        }}>
          Admin View - Showing attendance across all classes
        </div>
        
        <Link 
          to={`/admin/students/${studentId}`} 
          className="back-link"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
          Back to Admin Profile
        </Link>
      </div>

      <div className="summary-container">
        <h2>Attendance Summary</h2>
        <div className="percentage-container">
          <div className="percentage-value">{attendancePercentage}%</div>
          <div className="percentage-label">Attendance Rate</div>
        </div>
        
        <div className="stats-row">
          <div className="stat-item" style={{ backgroundColor: '#e8f5e9', borderRadius: '8px', padding: '16px' }}>
            <div className="stat-value" style={{ color: '#2e7d32' }}>{presentCount}</div>
            <div className="stat-label">Present</div>
          </div>
          <div className="stat-item" style={{ backgroundColor: '#ffebee', borderRadius: '8px', padding: '16px' }}>
            <div className="stat-value" style={{ color: '#d32f2f' }}>{absentCount}</div>
            <div className="stat-label">Absent</div>
          </div>
          <div className="stat-item" style={{ backgroundColor: '#fff8e1', borderRadius: '8px', padding: '16px' }}>
            <div className="stat-value" style={{ color: '#ff8f00' }}>{lateCount}</div>
            <div className="stat-label">Late</div>
          </div>
        </div>
      </div>

      <div className="attendance-list-container">
        <h2>Attendance History</h2>
        
        {error && (
          <div className="error-message">
            <p>Error loading attendance data: {error}</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {attendance.length > 0 ? (
          <div className="attendance-list">
            {attendance.map((record) => (
              <div key={record.id} className="attendance-card">
                <div className="status-indicator" style={{ backgroundColor: record.statusColor }}></div>
                <div className="attendance-content">
                  <div className="date-text">{record.date}</div>
                  <div className="halaqa-text">{record.className}</div>
                  <div 
                    className="status-badge"
                    style={{
                      color: record.statusColor,
                      backgroundColor: record.backgroundColor
                    }}
                  >
                    {record.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-attendance">
            <p>No attendance records found for this student.</p>
          </div>
        )}
      </div>
      
      {/* Admin-specific note */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fff3cd',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#856404',
        margin: '20px 0',
        border: '1px solid #ffeaa7'
      }}>
        <strong>Admin Note:</strong> This view shows attendance records across all classes. 
        Attendance management is handled by individual class teachers.
      </div>
    </div>
  );
};

export default AdminStudentAttendance;